// Shared focus guards for scan-panel pages.
//
// Background: HiddenScanInput captures hardware-scanner input via a
// global window.keydown listener that deliberately ignores keystrokes
// whose target is a BUTTON (or an element inside one). This is correct
// for keyboard-mode interaction with the on-screen keyboard, but it
// means a focused button after a tap silently swallows every
// subsequent scan until something else takes focus — operators have to
// tap empty space before they can scan again.
//
// We attack the problem from two angles, applied together at the
// scan-panel root so every descendant button is covered without
// having to touch each button individually:
//
//   <div
//     className="scan-panel"
//     onMouseDown={preventScanPageButtonFocus}
//     onClick={blurClickedScanPageButton}
//   >
//
//   1) preventScanPageButtonFocus (mousedown, preventive) —
//      preventDefault on the native mousedown for any button hit
//      stops the browser's default "focus the clicked element"
//      behaviour. This is the cleanest option but doesn't always
//      win on touch devices, where focus can latch on touchend
//      before mousedown fires.
//
//   2) blurClickedScanPageButton (click, reactive) — by the time
//      the click event bubbles, the click handler has run; we
//      blur the button so subsequent hardware-scanner keystrokes
//      go to <body> and HiddenScanInput's global keydown listener
//      picks them up. Mirrors the existing `.blur()` in
//      HiddenScanInput's camera-button onClick — same pattern,
//      lifted to the page level so it covers every button.
//
// Keyboard navigation (Tab focus + Enter/Space activation) is
// unaffected: Tab focus comes from focus events, not click; and
// Enter/Space activation fires onClick which then blurs the button
// — but the user is mid-keyboard-navigation, so they Tab again to
// reach the next control. That's the same behaviour as before this
// guard existed.
import type { MouseEvent } from "react";

export const preventScanPageButtonFocus = (e: MouseEvent<HTMLElement>): void => {
    const target = e.target as HTMLElement | null;
    if (target?.closest("button")) e.preventDefault();
};

export const blurClickedScanPageButton = (e: MouseEvent<HTMLElement>): void => {
    const target = e.target as HTMLElement | null;
    const btn = target?.closest("button");
    if (btn instanceof HTMLElement) btn.blur();
};
